import { useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GET_ALPHAPLAN_INTEGRATION_PARAMETER_DEFINITIONS } from './queries.ts';
import {
    OC_WRITE_MODE_DATES_AND_NOTES,
    OC_WRITE_MODE_OVERWRITE_PRICES_QUANTITIES,
    READ_MODE_REST,
    READ_MODE_SQL,
    type AlphaplanFormData,
    type AlphaplanIntegration,
    type AlphaplanParameterDefinition,
} from './types.ts';
import {
    validateBelegType,
    validateCaBundle,
    validateFieldName,
    validatePositionMemoField,
    type AlphaplanValidationError,
} from './validation.ts';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import JsonField from '../../../core/components/Fields/JsonField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import FormField from '../../../core/components/FormField.tsx';

const ADVANCED_FIELD_NAMES: (keyof AlphaplanFormData)[] = [
    'proxy',
    'caBundle',
    'supplierFilter',
    'supplierNumberField',
    'purchaseOrderBelegType',
    'openPoFilter',
    'readMode',
    'ocAbNumberField',
    'ocWriteMode',
    'ocWriteOnConvertedPo',
    'ocPositionMemoField',
    'ocHeaderMemoField',
    'ocToleranceBlock',
    'integrationParameters',
];

export interface AlphaplanAdvancedFieldsProps {
    form: UseFormReturn<AlphaplanFormData>;
    integration?: AlphaplanIntegration;
    toMessage: (error: AlphaplanValidationError | null) => string | true;
}

/**
 * The rarely touched half of the ALPHAPLAN connector configuration: connectivity, the purchase-order
 * lookup and how an order confirmation is written back. Collapsed by default; the defaults match a
 * standard ALPHAPLAN installation.
 */
const AlphaplanAdvancedFields = ({ form, integration, toMessage }: AlphaplanAdvancedFieldsProps) => {
    const { t } = useTranslation('integrations');
    const formErrors = form.formState.errors;
    const { submitCount } = form.formState;
    const detailsRef = useRef<HTMLDetailsElement>(null);

    // a rejected submit must not hide its errors inside the collapsed section
    const hasAdvancedErrors = ADVANCED_FIELD_NAMES.some((name) => !!formErrors[name]);
    useEffect(() => {
        if (hasAdvancedErrors && detailsRef.current) detailsRef.current.open = true;
    }, [hasAdvancedErrors, submitCount]);

    const { data: parameterDefinitionsData } = useQuery(GET_ALPHAPLAN_INTEGRATION_PARAMETER_DEFINITIONS);
    const parameterDefinitions: AlphaplanParameterDefinition[] =
        parameterDefinitionsData?.alphaplanIntegrationParameterDefinitions || [];

    const caBundleHelpText = integration?.hasCaBundle
        ? t('alphaplan.form.caBundleStored')
        : t('alphaplan.form.caBundleHelpText');

    return (
        <details ref={detailsRef} className="border border-solid border-line-weak rounded-lg">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-content-strong select-none">
                {t('alphaplan.form.advancedSection')}
            </summary>

            <div className="flex flex-col gap-6 border-t border-solid border-line-weak p-4">
                <Controller
                    name="proxy"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.proxy')}
                            error={formErrors.proxy}
                            helpText={t('alphaplan.form.proxyHelpText')}
                        >
                            <StringField {...field} maxLength={512} />
                        </FormField>
                    )}
                />

                <Controller
                    name="caBundle"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validateCaBundle(value)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.caBundle')}
                            error={formErrors.caBundle}
                            helpText={caBundleHelpText}
                        >
                            <TextField {...field} />
                        </FormField>
                    )}
                />

                <div className="text-sm font-semibold text-content-strong">
                    {t('alphaplan.form.header.purchaseOrders')}
                </div>

                <Controller
                    name="supplierFilter"
                    control={form.control}
                    rules={{ required: t('alphaplan.form.requiredError') }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.supplierFilter')}
                            error={formErrors.supplierFilter}
                            helpText={t('alphaplan.form.supplierFilterHelpText')}
                        >
                            <StringField {...field} maxLength={512} />
                        </FormField>
                    )}
                />

                <Controller
                    name="supplierNumberField"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validateFieldName(value)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.supplierNumberField')}
                            error={formErrors.supplierNumberField}
                            helpText={t('alphaplan.form.supplierNumberFieldHelpText')}
                        >
                            <StringField {...field} maxLength={64} />
                        </FormField>
                    )}
                />

                <Controller
                    name="purchaseOrderBelegType"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validateBelegType(value)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.purchaseOrderBelegType')}
                            error={formErrors.purchaseOrderBelegType}
                            helpText={t('alphaplan.form.purchaseOrderBelegTypeHelpText')}
                        >
                            <IntegerField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="openPoFilter"
                    control={form.control}
                    rules={{ required: t('alphaplan.form.requiredError') }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.openPoFilter')}
                            error={formErrors.openPoFilter}
                            helpText={t('alphaplan.form.openPoFilterHelpText')}
                        >
                            <StringField {...field} maxLength={512} />
                        </FormField>
                    )}
                />

                <Controller
                    name="readMode"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.readMode')}
                            error={formErrors.readMode}
                            helpText={t('alphaplan.form.readModeHelpText')}
                        >
                            <SelectField
                                {...field}
                                options={[
                                    {
                                        value: READ_MODE_SQL,
                                        label: t('alphaplan.form.readModeOptions.sql'),
                                        description: t('alphaplan.form.readModeOptions.sqlDescription'),
                                    },
                                    {
                                        value: READ_MODE_REST,
                                        label: t('alphaplan.form.readModeOptions.rest'),
                                        description: t('alphaplan.form.readModeOptions.restDescription'),
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                <div className="text-sm font-semibold text-content-strong">
                    {t('alphaplan.form.header.orderConfirmationWriteBack')}
                </div>

                <Controller
                    name="ocAbNumberField"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validateFieldName(value)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocAbNumberField')}
                            error={formErrors.ocAbNumberField}
                            helpText={t('alphaplan.form.ocAbNumberFieldHelpText')}
                        >
                            <StringField {...field} maxLength={64} />
                        </FormField>
                    )}
                />

                <Controller
                    name="ocWriteMode"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocWriteMode')}
                            error={formErrors.ocWriteMode}
                            helpText={t('alphaplan.form.ocWriteModeHelpText')}
                        >
                            <SelectField
                                {...field}
                                options={[
                                    {
                                        value: OC_WRITE_MODE_DATES_AND_NOTES,
                                        label: t('alphaplan.form.ocWriteModeOptions.datesAndNotes'),
                                        description: t('alphaplan.form.ocWriteModeOptions.datesAndNotesDescription'),
                                    },
                                    {
                                        value: OC_WRITE_MODE_OVERWRITE_PRICES_QUANTITIES,
                                        label: t('alphaplan.form.ocWriteModeOptions.overwritePricesQuantities'),
                                        description: t(
                                            'alphaplan.form.ocWriteModeOptions.overwritePricesQuantitiesDescription'
                                        ),
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="ocWriteOnConvertedPo"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocWriteOnConvertedPo')}
                            error={formErrors.ocWriteOnConvertedPo}
                            helpText={t('alphaplan.form.ocWriteOnConvertedPoHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="ocPositionMemoField"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validatePositionMemoField(value)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocPositionMemoField')}
                            error={formErrors.ocPositionMemoField}
                            helpText={t('alphaplan.form.ocPositionMemoFieldHelpText')}
                        >
                            <StringField {...field} maxLength={64} />
                        </FormField>
                    )}
                />

                <Controller
                    name="ocHeaderMemoField"
                    control={form.control}
                    rules={{ validate: (value) => toMessage(validateFieldName(value, true)) }}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocHeaderMemoField')}
                            error={formErrors.ocHeaderMemoField}
                            helpText={t('alphaplan.form.ocHeaderMemoFieldHelpText')}
                        >
                            <StringField {...field} maxLength={64} />
                        </FormField>
                    )}
                />

                <Controller
                    name="ocToleranceBlock"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.ocToleranceBlock')}
                            error={formErrors.ocToleranceBlock}
                            helpText={t('alphaplan.form.ocToleranceBlockHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="integrationParameters"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('alphaplan.form.integrationParameters')}
                            error={formErrors.integrationParameters}
                            helpText={
                                <span>
                                    {t('alphaplan.form.integrationParametersHelpText')}
                                    {parameterDefinitions.length > 0 && (
                                        <ul className="list-disc pl-4 mt-1">
                                            {parameterDefinitions.map((definition) => (
                                                <li key={definition.key}>
                                                    <code>{definition.key}</code> ({definition.type},{' '}
                                                    {t('alphaplan.form.integrationParameterDefault')}:{' '}
                                                    <code>{definition.default ?? 'null'}</code>) —{' '}
                                                    {definition.description}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </span>
                            }
                        >
                            <JsonField value={field.value} onValueChange={(value) => field.onChange(value)} />
                        </FormField>
                    )}
                />
            </div>
        </details>
    );
};

export default AlphaplanAdvancedFields;
